import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft,
  LayoutGrid,
  List,
  Plus,
  Clock,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Trash2,
  RotateCcw,
  Paperclip,
  Upload,
  X,
  Calendar,
  Play,
  MoreVertical,
  Maximize2,
  Minimize2,
  Download,
  ExternalLink,
  AlertCircle,
  MessageSquare,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import { coworkApi, API_BASE_URL } from '../api/coworkApi';
import { avatarSrc } from '../avatarSrc';
import { PresentationModal } from './PresentationModal';
import { isEmbedMode } from '@/utils/urlParams';
import type { Agent, Project, Task, TaskDeliverable, TaskAttachment, TaskComment, TaskStatus, TaskPriority } from '../types/cowork';

// ─────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────

const COLUMNS: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'queue', label: 'Queue', color: '#6366f1' },
  { status: 'working', label: 'Working', color: '#f59e0b' },
  { status: 'review', label: 'Review', color: '#8b5cf6' },
  { status: 'done', label: 'Done', color: '#10b981' },
];

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#98a2b3', bg: '#f9fafb' },
  medium: { label: 'Medium', color: '#3b82f6', bg: '#eff6ff' },
  high: { label: 'High', color: '#f97316', bg: '#fff7ed' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#fef2f2' },
};

// ── Agent avatar helper — uses avatarUrl when available, falls back to emoji ──
function AgentAvatar({ agent, size = 16, className }: {
  agent: { avatarEmoji?: string; avatarUrl?: string; name?: string; accentColor?: string };
  size?: number;
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);
  const url = avatarSrc(agent) ?? null;

  if (url && !imgError) {
    return (
      <img
        src={url}
        alt={agent.name || ''}
        className={cn('rounded-full object-cover', className)}
        style={{ width: size, height: size }}
        onError={() => setImgError(true)}
      />
    );
  }
  return <span className={className} style={{ fontSize: size * 0.75, lineHeight: 1 }}>{agent.avatarEmoji || '🤖'}</span>;
}

const STATUS_ICONS: Record<string, typeof Clock> = {
  queue: Clock,
  working: Loader2,
  waiting: Clock,
  review: ChevronRight,
  done: CheckCircle2,
  failed: AlertCircle,
};

// ─────────────────────────────────────────────
//  Deliverable Text Renderer
//  - Converts `/assets/...` paths into inline images
//  - Renders markdown with clean, compact styling
// ─────────────────────────────────────────────

function DeliverableText({ content }: { content: string }) {
  // Split content into segments: text blocks and embedded asset image URLs
  const assetRegex = /(?:`)?\/assets\/[^\s`"')\]]+\.(?:png|jpe?g|gif|webp|svg)(?:`)?/gi;
  const parts: { type: 'text' | 'image'; value: string }[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = assetRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    // Strip backticks from the URL
    const url = match[0].replace(/`/g, '');
    parts.push({ type: 'image', value: url });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }

  // If no asset URLs found, render the whole thing as markdown
  if (parts.length === 1 && parts[0].type === 'text') {
    return (
      <div className="task-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {parts.map((part, i) =>
        part.type === 'image' ? (
          <img
            key={i}
            src={`${API_BASE_URL}${part.value}`}
            alt="Generated visual"
            className="rounded-lg max-w-full border border-gray-100"
          />
        ) : part.value.trim() ? (
          <div key={i} className="task-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.value}</ReactMarkdown>
          </div>
        ) : null
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Context Menu (Force Start / Hold / Delete)
// ─────────────────────────────────────────────

function TaskContextMenu({
  task,
  anchorRef,
  onForceStart,
  onDelete,
  onClose,
}: {
  task: Task;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onForceStart: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Position menu near the anchor
  const rect = anchorRef.current?.getBoundingClientRect();
  const style: React.CSSProperties = rect
    ? { position: 'fixed', top: rect.bottom + 4, left: rect.right - 160, zIndex: 100 }
    : { position: 'fixed', top: 100, left: 100, zIndex: 100 };

  return (
    <div ref={menuRef} style={style} className="w-[160px] bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden">
      {task.status !== 'working' && (
        <button
          onClick={onForceStart}
          className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#344054] hover:bg-[#f9fafb] transition-colors text-left"
          style={{ fontWeight: 500 }}
        >
          <Play className="w-3 h-3 text-green-500" />
          Force Start
        </button>
      )}
      <button
        onClick={onDelete}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-red-500 hover:bg-red-50 transition-colors text-left"
        style={{ fontWeight: 500 }}
      >
        <Trash2 className="w-3 h-3" />
        Delete
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Task Card (used in both views)
// ─────────────────────────────────────────────

function TaskCard({
  task,
  project,
  onClick,
  onForceStart,
  onDelete,
  onMarkDone,
}: {
  task: Task;
  project?: Project | null;
  onClick: () => void;
  onForceStart: () => void;
  onDelete: () => void;
  onMarkDone: () => void;
}) {
  const pri = PRIORITY_CONFIG[task.priority];
  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative w-full bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group">
      <button
        onClick={onClick}
        className="w-full text-left p-3"
      >
        {/* Title */}
        <div className="flex items-start gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-[#101828] leading-snug line-clamp-2" style={{ fontWeight: 600 }}>
              {task.title}
            </p>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Assigned agent */}
          {task.assignedAgent && (
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px]"
              style={{
                background: `${task.assignedAgent.accentColor}12`,
                color: task.assignedAgent.accentColor,
                fontWeight: 600,
              }}
            >
              <AgentAvatar agent={task.assignedAgent} size={16} />
              <span>{task.assignedAgent.name}</span>
            </div>
          )}

          {/* Created by */}
          {task.createdByAgent && task.createdByAgent.id !== task.assignedAgentId && (
            <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 500 }}>
              via {task.createdByAgent.name}
            </span>
          )}

          {/* Project */}
          {project && (
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-[#f3f4f6] text-[#667085] max-w-[120px]"
              style={{ fontWeight: 600 }}
              title={project.title}
            >
              <span>{project.emoji}</span>
              <span className="truncate">{project.title}</span>
            </span>
          )}

          {/* Priority badge */}
          <span
            className="px-1.5 py-0.5 rounded text-[10px]"
            style={{ background: pri.bg, color: pri.color, fontWeight: 600 }}
          >
            {pri.label}
          </span>

          {/* Deadline */}
          {task.deadline && (
            <span className={cn(
              'flex items-center gap-0.5 text-[10px]',
              isOverdue ? 'text-red-500' : 'text-[#98a2b3]'
            )} style={{ fontWeight: 500 }}>
              <Calendar className="w-2.5 h-2.5" />
              {new Date(task.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}

          {/* Working spinner */}
          {task.status === 'working' && (
            <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
            </span>
          )}

          {/* Waiting indicator */}
          {task.status === 'waiting' && (
            <span className="flex items-center gap-0.5 text-[10px] text-blue-500">
              <Clock className="w-2.5 h-2.5" />
            </span>
          )}

          {/* Deliverable count */}
          {(task.deliverableCount ?? 0) > 0 && (
            <span className="text-[10px] text-green-600" style={{ fontWeight: 500 }}>
              {task.deliverableCount} deliv.
            </span>
          )}
        </div>

        {/* Subtask progress bar */}
        {(task.subtaskCount ?? 0) > 0 && (() => {
          const total = task.subtaskCount!;
          const done = task.subtaskDoneCount ?? 0;
          const pct = Math.round((done / total) * 100);
          const isComplete = done >= total;
          return (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[#667085]" style={{ fontWeight: 500 }}>
                  {done}/{total} subtasks
                </span>
                <span className="text-[10px] text-[#98a2b3]">{pct}%</span>
              </div>
              <div className="w-full h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: isComplete ? '#10b981' : '#f59e0b',
                  }}
                />
              </div>
            </div>
          );
        })()}

        {/* Latest status log entry */}
        {task.statusLog && task.statusLog.length > 0 && (() => {
          const latest = task.statusLog[task.statusLog.length - 1];
          const STATUS_COLORS: Record<string, string> = {
            working: '#f59e0b',
            waiting: '#6366f1',
            progress: '#3b82f6',
            tool: '#3b82f6',
            review: '#10b981',
            failed: '#ef4444',
            recovered: '#f97316',
          };
          const color = STATUS_COLORS[latest.status] || '#98a2b3';
          const ago = (() => {
            const mins = Math.floor((Date.now() - new Date(latest.at).getTime()) / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.floor(hrs / 24)}d ago`;
          })();
          return (
            <div className="mt-2 pt-2 border-t border-gray-50">
              <div className="flex items-start gap-1.5">
                <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-[#667085] line-clamp-1" style={{ fontWeight: 500 }}>
                    {latest.message}
                  </p>
                  <p className="text-[9px] text-[#c4c9d4]">{ago}</p>
                </div>
              </div>
            </div>
          );
        })()}
      </button>

      {/* Quick actions — visible on hover */}
      <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
        {/* Mark done button */}
        {task.status !== 'done' && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkDone(); }}
            className="p-1 rounded-md text-[#c4c9d4] hover:text-green-500 hover:bg-green-50 transition-all"
            title="Mark as done"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}
        {/* More actions */}
        <button
          ref={menuBtnRef}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="p-1 rounded-md text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-50 transition-all"
        >
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </div>

      {menuOpen && (
        <TaskContextMenu
          task={task}
          anchorRef={menuBtnRef}
          onForceStart={() => { setMenuOpen(false); onForceStart(); }}
          onDelete={() => { setMenuOpen(false); onDelete(); }}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Task Detail Panel (slide-over)
// ─────────────────────────────────────────────

function TaskDetailPanel({
  taskId,
  agents,
  onClose,
  onUpdate,
  onDelete,
  brandId,
}: {
  taskId: string;
  agents: Agent[];
  onClose: () => void;
  onUpdate: () => void;
  onDelete: () => void;
  brandId?: string;
}) {
  const [task, setTask] = useState<Task | null>(null);
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [deliverables, setDeliverables] = useState<TaskDeliverable[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [changeRequestMode, setChangeRequestMode] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [typingAgents, setTypingAgents] = useState<string[]>([]);
  const [seenBy, setSeenBy] = useState<Array<{ agentId: string | null; agent: { id: string; name: string; avatarEmoji: string } | null; seenAt: string }>>([]);
  const [openPresentationId, setOpenPresentationId] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [selectedSubtaskId, setSelectedSubtaskId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Generation counter guards against stale responses: switching tasks bumps
  // the generation (via the effect cleanup below), so an in-flight response
  // for the previous task can't overwrite the new task's panel.
  const loadGenRef = useRef(0);
  const loadTask = useCallback(async () => {
    const gen = loadGenRef.current;
    try {
      const data = await coworkApi.tasks.get(taskId);
      if (gen !== loadGenRef.current) return; // stale response — ignore
      setTask(data.task);
      setSubtasks(data.subtasks);
      setDeliverables(data.deliverables);
      setAttachments(data.attachments);
      setComments(data.comments || []);
    } catch (err) {
      console.error('Failed to load task:', err);
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadTask();
    return () => { loadGenRef.current += 1; };
  }, [loadTask]);

  // Auto-refresh while working (pause when the tab is hidden)
  useEffect(() => {
    if (!task || (task.status !== 'working' && task.status !== 'queue' && task.status !== 'waiting')) return;
    const interval = setInterval(() => { if (!document.hidden) loadTask(); }, 8000);
    return () => clearInterval(interval);
  }, [task?.status, loadTask]);

  // Poll seen/typing status
  useEffect(() => {
    if (!task) return;
    const pollSeen = async () => {
      if (document.hidden) return;
      try {
        const data = await coworkApi.tasks.getSeen(task.id);
        const typing: string[] = [];
        const seen: typeof seenBy = [];
        for (const entry of data.seen) {
          if (entry.userId.startsWith('typing:')) {
            if (entry.agentId && entry.agent) typing.push(entry.agent.name);
          } else {
            seen.push(entry);
          }
        }
        setTypingAgents(typing);
        setSeenBy(seen);
      } catch { /* non-critical */ }
    };
    pollSeen();
    const interval = setInterval(pollSeen, 5000);
    return () => clearInterval(interval);
  }, [task?.id]);

  // Mark as seen when viewing
  useEffect(() => {
    if (task?.id) coworkApi.tasks.markSeen(task.id).catch(() => {});
  }, [task?.id, comments.length]);

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!task) return;
    await coworkApi.tasks.update(task.id, { status: newStatus });
    setTask(prev => prev ? { ...prev, status: newStatus } : prev);
    onUpdate();
  };

  const handleForceStart = async () => {
    if (!task) return;
    setExecuting(true);
    try {
      // Set status to working and trigger execution
      await coworkApi.tasks.update(task.id, { status: 'working' });
      await coworkApi.tasks.execute(task.id);
      setTask(prev => prev ? { ...prev, status: 'working' } : prev);
      onUpdate();
      await new Promise(resolve => setTimeout(resolve, 2000));
      loadTask();
    } catch (err) {
      console.error('Failed to start task:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start task');
    } finally {
      setExecuting(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    await coworkApi.tasks.delete(task.id);
    onDelete();
    onClose();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!task || !e.target.files?.[0]) return;
    const attachment = await coworkApi.tasks.uploadAttachment(task.id, e.target.files[0]);
    setAttachments(prev => [...prev, attachment]);
  };

  const handleApprove = async () => {
    if (!task) return;
    await coworkApi.tasks.update(task.id, { status: 'done' });
    setTask(prev => prev ? { ...prev, status: 'done' } : prev);
    toast.success('Task approved');
    onUpdate();
  };

  const handleSendComment = async () => {
    if (!task || !commentText.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      if (changeRequestMode) {
        // Review feedback: store it with the review-feedback role (the
        // executor injects those into the revision prompt) and re-queue.
        const comment = await coworkApi.tasks.addComment(task.id, commentText.trim(), 'review-feedback');
        setComments(prev => [...prev, comment]);
        await coworkApi.tasks.update(task.id, { status: 'queue' });
        setTask(prev => prev ? { ...prev, status: 'queue' } : prev);
        setChangeRequestMode(false);
        setCommentText('');
        toast.success('Changes requested: the agent will revise this task');
        onUpdate();
        return;
      }
      const comment = await coworkApi.tasks.addComment(task.id, commentText.trim());
      setComments(prev => [...prev, comment]);

      // Detect @mentions and trigger agent replies
      const mentionPattern = /@(\w+)/g;
      let match: RegExpExecArray | null;
      const mentionedIds: string[] = [];
      while ((match = mentionPattern.exec(commentText)) !== null) {
        const name = match[1].toLowerCase();
        const mentioned = agents.find(a => a.name.toLowerCase() === name);
        if (mentioned) mentionedIds.push(mentioned.id);
      }
      if (mentionedIds.length > 0) {
        coworkApi.tasks.mentionAgents(task.id, mentionedIds, brandId).catch(() => {});
        // Start polling for replies
        setTimeout(loadTask, 3000);
        setTimeout(loadTask, 6000);
        setTimeout(loadTask, 10000);
      }

      setCommentText('');
    } catch (err) {
      console.error('Failed to send comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const insertMention = (name: string) => {
    const input = commentInputRef.current;
    if (!input) return;
    const cursor = input.selectionStart || commentText.length;
    const before = commentText.slice(0, cursor);
    const after = commentText.slice(cursor);
    const atPos = before.lastIndexOf('@');
    if (atPos === -1) return;
    const newText = before.slice(0, atPos) + `@${name} ` + after;
    setCommentText(newText);
    setMentionQuery(null);
    // Refocus input
    setTimeout(() => {
      input.focus();
      const newCursor = atPos + name.length + 2; // @name + space
      input.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  const handleTriggerReview = async () => {
    if (!task) return;
    try {
      await coworkApi.tasks.triggerReview(task.id, brandId);
      loadTask();
    } catch (err) {
      console.error('Failed to trigger review:', err);
    }
  };

  // If a subtask is selected, show its detail instead
  if (selectedSubtaskId) {
    return (
      <TaskDetailPanel
        taskId={selectedSubtaskId}
        agents={agents}
        onClose={() => setSelectedSubtaskId(null)}
        onUpdate={() => { loadTask(); onUpdate(); }}
        onDelete={() => { setSelectedSubtaskId(null); loadTask(); onUpdate(); }}
        brandId={brandId}
      />
    );
  }

  if (loading || !task) {
    return (
      <div className="w-full h-full bg-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#98a2b3]" />
      </div>
    );
  }

  const statusCol = COLUMNS.find(c => c.status === task.status);
  const pri = PRIORITY_CONFIG[task.priority];

  const panelClasses = isFullScreen
    ? 'fixed inset-0 z-50 bg-white flex flex-col overflow-hidden'
    : 'w-full h-full bg-white flex flex-col overflow-hidden';

  return (
    <>
    {openPresentationId && (
      <PresentationModal presentationId={openPresentationId} onClose={() => setOpenPresentationId(null)} />
    )}
    <div className={panelClasses}>
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { if (isFullScreen) setIsFullScreen(false); else onClose(); }}
            className="text-[#98a2b3] hover:text-[#344054] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>Task Detail</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Force Start */}
          {task.status !== 'working' && (
            <button
              onClick={handleForceStart}
              disabled={executing}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
              style={{ fontWeight: 500 }}
              title="Force start execution"
            >
              {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Start
            </button>
          )}
          {/* Re-run */}
          <button
            onClick={handleForceStart}
            disabled={executing || task.status === 'working'}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-[#667085] hover:bg-gray-50 transition-colors disabled:opacity-50"
            style={{ fontWeight: 500 }}
            title="Re-run task"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          {/* Delete */}
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-red-400 hover:bg-red-50 transition-colors"
            style={{ fontWeight: 500 }}
            title="Delete task"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          {/* Fullscreen toggle */}
          <div className="w-px h-4 bg-gray-100 mx-0.5" />
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-[#667085] hover:bg-gray-50 transition-colors"
            title={isFullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullScreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="shrink-0 flex items-center justify-between px-4 py-2 bg-red-50 border-b border-red-100">
          <span className="text-[12px] text-red-600" style={{ fontWeight: 500 }}>Delete this task and all deliverables?</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-2.5 py-1 rounded-lg text-[11px] text-[#667085] hover:bg-white transition-colors"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-2.5 py-1 rounded-lg text-[11px] text-white bg-red-500 hover:bg-red-600 transition-colors"
              style={{ fontWeight: 500 }}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 min-w-0">
        {/* Title */}
        <h2 className="text-[16px] text-[#101828] leading-snug" style={{ fontWeight: 700 }}>
          {task.title}
        </h2>

        {/* Status + Priority + Agent row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status dropdown */}
          <select
            value={task.status}
            onChange={e => handleStatusChange(e.target.value as TaskStatus)}
            className="text-[11px] px-2 py-1 rounded-lg border border-gray-200 bg-white cursor-pointer"
            style={{ fontWeight: 600, color: statusCol?.color }}
          >
            {COLUMNS.map(c => (
              <option key={c.status} value={c.status}>{c.label}</option>
            ))}
          </select>

          {/* Priority */}
          <span className="px-2 py-0.5 rounded text-[11px]" style={{ background: pri.bg, color: pri.color, fontWeight: 600 }}>
            {pri.label}
          </span>

          {/* Assigned agent */}
          {task.assignedAgent && (
            <div
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[11px]"
              style={{ background: `${task.assignedAgent.accentColor}10`, fontWeight: 600 }}
            >
              <AgentAvatar agent={task.assignedAgent} size={16} />
              <span style={{ color: task.assignedAgent.accentColor }}>{task.assignedAgent.name}</span>
            </div>
          )}

          {/* Working indicator */}
          {task.status === 'working' && (
            <span className="flex items-center gap-1 text-[11px] text-amber-600">
              <Loader2 className="w-3 h-3 animate-spin" />
              Working…
            </span>
          )}
        </div>

        {/* Review actions — the main loop for a task awaiting sign-off */}
        {task.status === 'review' && (
          <div className="flex items-center gap-2 bg-[#f5f3ff] border border-[#8b5cf6]/20 rounded-xl px-4 py-3">
            <ShieldCheck className="w-4 h-4 text-[#8b5cf6] shrink-0" />
            <span className="text-[12px] text-[#6d28d9] flex-1" style={{ fontWeight: 500 }}>
              Ready for your review
            </span>
            <button
              onClick={() => {
                setChangeRequestMode(true);
                setTimeout(() => commentInputRef.current?.focus(), 0);
              }}
              className="px-3 py-1.5 rounded-lg text-[12px] text-amber-700 bg-amber-100 hover:bg-amber-200 transition-colors"
              style={{ fontWeight: 600 }}
            >
              Request changes
            </button>
            <button
              onClick={handleApprove}
              className="px-3 py-1.5 rounded-lg text-[12px] text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              style={{ fontWeight: 600 }}
            >
              Approve
            </button>
          </div>
        )}

        {/* Deadline */}
        {task.deadline && (
          <div className="flex items-center gap-1.5 text-[12px] text-[#667085]">
            <Calendar className="w-3.5 h-3.5" />
            Deadline: {new Date(task.deadline).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        {/* Description */}
        {task.description && (
          <div className="bg-[#f9fafb] rounded-xl p-4 border border-gray-100/60">
            <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest mb-2" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>Brief</p>
            <div className="task-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.description}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Subtasks */}
        {subtasks.length > 0 && (() => {
          const SUBTASK_STATUS: Record<string, { color: string; bg: string; label: string }> = {
            queue:   { color: '#98a2b3', bg: '#f9fafb', label: 'Queued' },
            working: { color: '#f59e0b', bg: '#fffbeb', label: 'Working' },
            waiting: { color: '#6366f1', bg: '#eef2ff', label: 'Waiting' },
            review:  { color: '#8b5cf6', bg: '#f5f3ff', label: 'Review' },
            done:    { color: '#10b981', bg: '#ecfdf5', label: 'Done' },
            failed:  { color: '#ef4444', bg: '#fef2f2', label: 'Failed' },
          };
          const doneCount = subtasks.filter(s => s.status === 'done' || s.status === 'review').length;
          return (
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                  Subtasks ({subtasks.length})
                </p>
                <span className="text-[11px] text-[#667085] tabular-nums" style={{ fontWeight: 600 }}>
                  {doneCount}/{subtasks.length} complete
                </span>
              </div>
              {/* Progress bar */}
              <div className="w-full h-[4px] bg-gray-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round((doneCount / subtasks.length) * 100)}%`,
                    background: doneCount >= subtasks.length ? '#10b981' : '#f59e0b',
                  }}
                />
              </div>
              <div className="space-y-1">
                {subtasks.map(sub => {
                  const st = SUBTASK_STATUS[sub.status] || SUBTASK_STATUS.queue;
                  const isActive = sub.status === 'working';
                  const latestLog = sub.statusLog && sub.statusLog.length > 0 ? sub.statusLog[sub.statusLog.length - 1] : null;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => setSelectedSubtaskId(sub.id)}
                      className={cn(
                        'w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all',
                        isActive ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100 hover:bg-[#f9fafb]'
                      )}
                    >
                      {/* Status indicator */}
                      {isActive ? (
                        <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" style={{ color: st.color }} />
                      ) : sub.status === 'done' || sub.status === 'review' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: st.color }} />
                      ) : sub.status === 'failed' ? (
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" style={{ color: st.color }} />
                      ) : (
                        <span className="w-3 h-3 rounded-full shrink-0 border-2" style={{ borderColor: st.color }} />
                      )}
                      {/* Title + status */}
                      <div className="flex-1 min-w-0">
                        <span className={cn(
                          'text-[12px] block truncate',
                          (sub.status === 'done' || sub.status === 'review') ? 'text-[#98a2b3] line-through' : 'text-[#344054]'
                        )} style={{ fontWeight: 500 }}>
                          {sub.title}
                        </span>
                        {isActive && latestLog && (
                          <span className="text-[10px] text-amber-600 block truncate mt-0.5">
                            {latestLog.message}
                          </span>
                        )}
                      </div>
                      {/* Status badge */}
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0 uppercase tracking-wider"
                        style={{ background: st.bg, color: st.color, fontWeight: 600 }}
                      >
                        {st.label}
                      </span>
                      {sub.assignedAgent && (
                        <span className="shrink-0" title={sub.assignedAgent.name}>
                          <AgentAvatar agent={sub.assignedAgent} size={14} />
                        </span>
                      )}
                      <ChevronRight className="w-3 h-3 text-[#c4c9d4] shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Activity Log */}
        {task.statusLog && task.statusLog.length > 0 && (() => {
          const STATUS_COLORS: Record<string, string> = {
            working: '#f59e0b', waiting: '#6366f1', progress: '#3b82f6',
            tool: '#3b82f6', review: '#10b981', failed: '#ef4444',
            recovered: '#f97316', queue: '#98a2b3', done: '#10b981',
          };
          // Collapse consecutive duplicate messages
          type CollapsedEntry = { message: string; status: string; at: string; count: number; lastAt: string };
          const collapsed: CollapsedEntry[] = [];
          for (const entry of task.statusLog!) {
            const prev = collapsed[collapsed.length - 1];
            if (prev && prev.message === entry.message && prev.status === entry.status) {
              prev.count++;
              prev.lastAt = entry.at;
            } else {
              collapsed.push({ message: entry.message, status: entry.status, at: entry.at, count: 1, lastAt: entry.at });
            }
          }
          return (
            <div>
              <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest mb-2.5" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Activity ({task.statusLog!.length})
              </p>
              <div className="space-y-0 max-h-[240px] overflow-y-auto pr-1">
                {collapsed.map((entry, i) => {
                  const color = STATUS_COLORS[entry.status] || '#98a2b3';
                  const isFailed = entry.status === 'failed';
                  return (
                    <div key={i} className="flex items-start gap-2.5 relative">
                      {i < collapsed.length - 1 && (
                        <div className="absolute left-[4px] top-[14px] w-px h-[calc(100%)]" style={{ background: '#f0f1f3' }} />
                      )}
                      <div className="w-[8px] h-[8px] rounded-full mt-[5px] shrink-0 relative z-10 border-2" style={{ borderColor: color, background: i === collapsed.length - 1 ? color : 'white' }} />
                      <div className="pb-2 flex-1 min-w-0">
                        <p className={cn('text-[12px]', isFailed ? 'text-red-500' : 'text-[#344054]')} style={{ fontWeight: 500 }}>
                          {entry.message}
                          {entry.count > 1 && (
                            <span className="ml-1.5 text-[10px] text-[#98a2b3] bg-gray-100 px-1.5 py-0.5 rounded-full" style={{ fontWeight: 600 }}>
                              ×{entry.count}
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-[#c4c9d4] mt-0.5">
                          {new Date(entry.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {entry.count > 1 && ` → ${new Date(entry.lastAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Deliverables */}
        {deliverables.length > 0 && (
          <div>
            <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest mb-2.5" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              Deliverables ({deliverables.length})
            </p>
            <div className="space-y-3">
              {deliverables.map(d => {
                const delivAgent = agents.find(a => a.id === d.agentId);
                return (
                  <div key={d.id} className="rounded-xl border border-gray-100 overflow-hidden">
                    {/* Deliverable header */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-[#f9fafb] border-b border-gray-100">
                      {delivAgent && (
                        <AgentAvatar agent={delivAgent} size={16} />
                      )}
                      <span className="text-[11px] text-[#667085]" style={{ fontWeight: 600 }}>
                        {delivAgent?.name ?? d.agentId}
                      </span>
                      <span className="text-[10px] text-[#c4c9d4]">
                        {new Date(d.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    {/* Deliverable content */}
                    <div className="px-3 py-3 overflow-x-auto">
                      {d.type === 'image' ? (
                        <div className="relative group">
                          <img
                            src={d.content.startsWith('http') ? d.content : `${API_BASE_URL}${d.content}`}
                            alt="Deliverable"
                            className="rounded-lg max-w-full"
                          />
                          <a
                            href={d.content.startsWith('http') ? d.content : `${API_BASE_URL}${d.content}`}
                            download
                            className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] bg-black/60 text-white hover:bg-black/80 transition-all opacity-0 group-hover:opacity-100 backdrop-blur-sm"
                            style={{ fontWeight: 600 }}
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </a>
                        </div>
                      ) : d.type === 'presentation' ? (
                        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                          <span className="text-2xl">📊</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                              {(d.metadata?.title as string) ?? 'Presentation'}
                            </div>
                            <div className="text-[10px] text-[#98a2b3]">
                              HTML presentation • Export to PDF or PPTX
                            </div>
                          </div>
                          <button
                            onClick={() => setOpenPresentationId((d.metadata?.presentationId as string) ?? d.content)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[#be43ff] text-white hover:bg-[#a835e0] transition-colors shrink-0"
                            style={{ fontWeight: 600 }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open
                          </button>
                        </div>
                      ) : d.type === 'document' ? (
                        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                          <span className="text-2xl">
                            {d.metadata?.format === 'slides' || d.metadata?.format === 'pptx' ? '📊' : d.metadata?.format === 'xlsx' ? '📈' : '📝'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                              {(d.metadata?.title as string) ?? 'Document'}
                            </div>
                            <div className="text-[10px] text-[#98a2b3]">
                              {d.metadata?.format === 'slides' || d.metadata?.format === 'pptx' ? 'Presentation' : d.metadata?.format === 'xlsx' ? 'Excel document' : 'Word document'}
                            </div>
                          </div>
                          <a
                            href={d.content.startsWith('http') ? d.content : `${API_BASE_URL}${d.content}`}
                            download
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[#be43ff] text-white hover:bg-[#a835e0] transition-colors shrink-0"
                            style={{ fontWeight: 600 }}
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </a>
                        </div>
                      ) : (
                        <DeliverableText content={d.content} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Attachments */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              Attachments ({attachments.length})
            </p>
            <label className="flex items-center gap-1 text-[11px] text-[#667085] hover:text-[#344054] cursor-pointer transition-colors">
              <Upload className="w-3 h-3" />
              Upload
              <input type="file" className="hidden" onChange={handleFileUpload} />
            </label>
          </div>
          {attachments.length > 0 && (
            <div className="space-y-1">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#f9fafb]">
                  <Paperclip className="w-3 h-3 text-[#98a2b3]" />
                  <span className="text-[12px] text-[#344054] flex-1 truncate" style={{ fontWeight: 500 }}>
                    {a.fileName}
                  </span>
                  <span className="text-[10px] text-[#c4c9d4]">
                    {new Date(a.uploadedAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments & Review Thread */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] text-[#98a2b3] uppercase tracking-widest" style={{ fontWeight: 700, letterSpacing: '0.08em' }}>
              <MessageSquare className="w-3 h-3 inline mr-1" />
              Discussion ({comments.length})
            </p>
            {task.status === 'review' && task.createdByAgentId && task.createdByAgentId !== task.assignedAgentId && (
              <button
                onClick={handleTriggerReview}
                className="flex items-center gap-1 text-[11px] text-[#8b5cf6] hover:text-[#7c3aed] transition-colors"
                style={{ fontWeight: 500 }}
              >
                <ShieldCheck className="w-3 h-3" />
                Request Review
              </button>
            )}
          </div>

          {/* Comment list */}
          {comments.length > 0 && (
            <div className="space-y-2 mb-3 max-h-[300px] overflow-y-auto" id="task-comments-list">
              {comments.map(c => {
                const isReviewFeedback = c.role === 'review-feedback';
                const isApproved = c.role === 'review-approved';
                const isAgent = !!c.agent;
                return (
                  <div
                    key={c.id}
                    className={cn(
                      'px-3 py-2.5 rounded-lg text-[12px]',
                      isReviewFeedback ? 'bg-amber-50 border border-amber-200' :
                      isApproved ? 'bg-emerald-50 border border-emerald-200' :
                      isAgent ? 'bg-[#f9fafb]' : 'bg-blue-50'
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      {isAgent && c.agent ? (
                        <>
                          <AgentAvatar agent={c.agent} size={16} />
                          <span className="text-[11px]" style={{ fontWeight: 600, color: c.agent.accentColor }}>
                            {c.agent.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-[11px] text-[#344054]" style={{ fontWeight: 600 }}>You</span>
                      )}
                      {isReviewFeedback && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" style={{ fontWeight: 600 }}>
                          Changes Requested
                        </span>
                      )}
                      {isApproved && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" style={{ fontWeight: 600 }}>
                          Approved
                        </span>
                      )}
                      <span className="text-[10px] text-[#c4c9d4] ml-auto">
                        {new Date(c.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="text-[12px] text-[#344054] task-markdown">
                      {c.content.includes('**') || c.content.includes('#') || c.content.includes('- ') ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{c.content}</ReactMarkdown>
                      ) : (
                        c.content.split(/(@\w+)/g).map((part, pi) =>
                          part.startsWith('@') ? (
                            <span key={pi} className="text-[#be43ff] font-medium">{part}</span>
                          ) : (
                            <span key={pi}>{part}</span>
                          )
                        )
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={commentsEndRef} />
            </div>
          )}

          {/* Typing indicator */}
          {typingAgents.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#be43ff] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#be43ff] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#be43ff] animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              <span className="text-[11px] text-[#98a2b3]">
                {typingAgents.join(', ')} {typingAgents.length === 1 ? 'is' : 'are'} typing...
              </span>
            </div>
          )}

          {/* Seen by */}
          {seenBy.length > 0 && (
            <div className="flex items-center gap-1 mb-2 px-1">
              <span className="text-[10px] text-[#c4c9d4]">Seen by</span>
              {seenBy.slice(0, 5).map((s, i) => (
                <span key={i} title={s.agent?.name || 'You'}>
                  {s.agent ? <AgentAvatar agent={s.agent} size={14} /> : <span className="text-[10px]">👤</span>}
                </span>
              ))}
              {seenBy.length > 5 && (
                <span className="text-[10px] text-[#c4c9d4]">+{seenBy.length - 5}</span>
              )}
            </div>
          )}

          {/* Change-request mode indicator */}
          {changeRequestMode && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <span className="text-[11px] text-amber-700 flex-1" style={{ fontWeight: 500 }}>
                Describe what should change: the agent will revise the task with your feedback.
              </span>
              <button
                onClick={() => setChangeRequestMode(false)}
                className="text-[11px] text-amber-600 hover:text-amber-800"
                style={{ fontWeight: 600 }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Comment input with @mention autocomplete */}
          <div className="relative">
            <div className="flex items-center gap-2">
              <input
                ref={commentInputRef}
                type="text"
                value={commentText}
                onChange={e => {
                  const val = e.target.value;
                  setCommentText(val);
                  // Detect @mention trigger
                  const cursor = e.target.selectionStart || val.length;
                  const before = val.slice(0, cursor);
                  const atMatch = before.match(/@(\w*)$/);
                  if (atMatch) {
                    setMentionQuery(atMatch[1].toLowerCase());
                    setMentionIndex(0);
                  } else {
                    setMentionQuery(null);
                  }
                }}
                onKeyDown={e => {
                  // Handle mention dropdown navigation
                  if (mentionQuery !== null) {
                    const filtered = agents.filter(a => a.name.toLowerCase().startsWith(mentionQuery || ''));
                    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, filtered.length - 1)); return; }
                    if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
                    if ((e.key === 'Enter' || e.key === 'Tab') && filtered.length > 0) {
                      e.preventDefault();
                      insertMention(filtered[mentionIndex].name);
                      return;
                    }
                    if (e.key === 'Escape') { setMentionQuery(null); return; }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendComment(); }
                }}
                placeholder={changeRequestMode ? 'What needs to change?' : 'Add a comment... (type @ to mention)'}
                className={cn(
                  'flex-1 text-[12px] px-3 py-2 rounded-lg border bg-white outline-none focus:ring-1 placeholder:text-[#c4c9d4]',
                  changeRequestMode
                    ? 'border-amber-300 focus:ring-amber-300/40'
                    : 'border-gray-200 focus:ring-[#be43ff]/30'
                )}
              />
              <button
                onClick={handleSendComment}
                disabled={!commentText.trim() || sendingComment}
                className="p-2 rounded-lg text-white bg-[#be43ff] hover:bg-[#a835e0] disabled:opacity-40 transition-colors"
              >
                {sendingComment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* @mention dropdown */}
            {mentionQuery !== null && (() => {
              const filtered = agents.filter(a => a.name.toLowerCase().startsWith(mentionQuery || ''));
              if (filtered.length === 0) return null;
              return (
                <div className="absolute bottom-full left-0 mb-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
                  {filtered.map((a, i) => (
                    <button
                      key={a.id}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-[12px] text-left transition-colors',
                        i === mentionIndex ? 'bg-[#be43ff]/10 text-[#be43ff]' : 'hover:bg-gray-50 text-[#344054]'
                      )}
                      onMouseDown={e => { e.preventDefault(); insertMention(a.name); }}
                      onMouseEnter={() => setMentionIndex(i)}
                    >
                      <AgentAvatar agent={a} size={18} />
                      <span style={{ fontWeight: 500 }}>{a.name}</span>
                      <span className="text-[10px] text-[#98a2b3] ml-auto">{a.title}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Created info */}
        <div className="text-[10px] text-[#d0d5dd] pt-2 border-t border-gray-50">
          Created {new Date(task.createdAt).toLocaleString()}
          {task.createdByAgent && ` by ${task.createdByAgent.name}`}
        </div>
      </div>
    </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  Create Task Modal
// ─────────────────────────────────────────────

function CreateTaskModal({
  agents,
  onClose,
  onCreate,
  brandId,
}: {
  agents: Agent[];
  onClose: () => void;
  onCreate: (task: Task) => void;
  brandId?: string;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState(agents[0]?.id ?? '');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [deadline, setDeadline] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!title.trim() || !assignedAgentId) return;
    setCreating(true);
    try {
      const task = await coworkApi.tasks.create({
        title: title.trim(),
        description: description.trim() || undefined,
        assignedAgentId,
        priority,
        deadline: deadline || undefined,
        brandId,
      });
      onCreate(task);
      onClose();
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-[480px] max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>New Task</h3>
          <button onClick={onClose} className="text-[#98a2b3] hover:text-[#344054]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 600 }}>Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Create Facebook ad banner for product launch"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:border-[#be43ff] transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 600 }}>Description / Brief</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Detailed instructions for the agent..."
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-[#101828] placeholder:text-[#c4c9d4] focus:outline-none focus:border-[#be43ff] transition-colors resize-none"
            />
          </div>

          {/* Assign to agent */}
          <div>
            <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 600 }}>Assign to</label>
            <div className="grid grid-cols-4 gap-2">
              {agents.filter(a => a.isActive).map(a => (
                <button
                  key={a.id}
                  onClick={() => setAssignedAgentId(a.id)}
                  className={cn(
                    'flex flex-col items-center gap-1 px-2 py-2 rounded-xl border transition-all text-center',
                    assignedAgentId === a.id
                      ? 'border-[#be43ff]/30 bg-[#f8f5ff]'
                      : 'border-gray-100 hover:bg-[#f9fafb]'
                  )}
                >
                  <AgentAvatar agent={a} size={24} />
                  <span className="text-[10px] text-[#344054] truncate w-full" style={{ fontWeight: 500 }}>
                    {a.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Priority + Deadline row */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 600 }}>Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value as TaskPriority)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-[#101828] focus:outline-none focus:border-[#be43ff]"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-[11px] text-[#667085] mb-1" style={{ fontWeight: 600 }}>Deadline</label>
              <input
                type="date"
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] text-[#101828] focus:outline-none focus:border-[#be43ff]"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-[13px] text-[#667085] hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || !assignedAgentId || creating}
            className="px-4 py-2 rounded-xl text-[13px] text-white bg-[#be43ff] hover:bg-[#a035d9] transition-colors disabled:opacity-50"
            style={{ fontWeight: 600 }}
          >
            {creating ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Creating…
              </span>
            ) : (
              'Create & Assign'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Main TaskBoardPage
// ─────────────────────────────────────────────

export function TaskBoardPage({
  agents,
  onBack,
  brandId,
  initialTaskId,
  onTaskViewed,
  initialShowCreate,
  onCreateConsumed,
}: {
  agents: Agent[];
  onBack: () => void;
  brandId?: string;
  initialTaskId?: string;
  onTaskViewed?: () => void;
  initialShowCreate?: boolean;
  onCreateConsumed?: () => void;
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [filterAgentId, setFilterAgentId] = useState<string | null>(null);
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(initialTaskId || null);
  const [showCreateModal, setShowCreateModal] = useState(initialShowCreate ?? false);

  // "New task" in the sidebar lands here with the modal pre-opened; tell the
  // parent we consumed the request so revisiting Tasks doesn't reopen it.
  useEffect(() => {
    if (initialShowCreate) onCreateConsumed?.();
  }, [initialShowCreate, onCreateConsumed]);

  // Auto-open task from external navigation (e.g., "View in Task Board" CTA)
  useEffect(() => {
    if (initialTaskId) {
      setSelectedTaskId(initialTaskId);
      onTaskViewed?.();
    }
  }, [initialTaskId]);

  const loadTasks = useCallback(async () => {
    try {
      const data = await coworkApi.tasks.list(undefined, filterAgentId ?? undefined, {
        projectId: filterProjectId ?? undefined,
      });
      setTasks(data.tasks);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filterAgentId, filterProjectId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Load projects for the filter dropdown + card badges
  useEffect(() => {
    coworkApi.projects.list({ brandId })
      .then(data => setProjects(data.projects))
      .catch(err => console.error('Failed to load projects:', err));
  }, [brandId]);

  // Auto-refresh every 15s if any task is working (skip while the tab is hidden)
  useEffect(() => {
    const hasActive = tasks.some(t => t.status === 'working' || t.status === 'queue' || t.status === 'waiting');
    if (!hasActive) return;
    const interval = setInterval(() => { if (!document.hidden) loadTasks(); }, 15_000);
    return () => clearInterval(interval);
  }, [tasks, loadTasks]);

  const filteredTasks = tasks.filter(t =>
    (!filterAgentId || t.assignedAgentId === filterAgentId) &&
    (!filterProjectId || t.projectId === filterProjectId)
  );

  const projectsById = new Map(projects.map(p => [p.id, p]));

  const handleForceStart = async (taskId: string) => {
    try {
      await coworkApi.tasks.update(taskId, { status: 'working' });
      await coworkApi.tasks.execute(taskId);
      loadTasks();
    } catch (err) {
      console.error('Failed to start task:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await coworkApi.tasks.delete(taskId);
      if (selectedTaskId === taskId) setSelectedTaskId(null);
      loadTasks();
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const handleMarkDone = async (taskId: string) => {
    try {
      await coworkApi.tasks.update(taskId, { status: 'done' });
      loadTasks();
    } catch (err) {
      console.error('Failed to mark task done:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to mark task done');
    }
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Header bar */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100">
        <div className="flex items-center gap-3">
          {/* In the back office the sidebar navigates; there is nothing for "Back" to go to. */}
          {!isEmbedMode() && (<>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[12px] text-[#98a2b3] hover:text-[#344054] transition-colors"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <div className="w-px h-4 bg-gray-100" />
          </>)}
          <span className="text-[14px] text-[#101828]" style={{ fontWeight: 700 }}>Task Board</span>
          <span className="text-[12px] text-[#98a2b3]">{filteredTasks.length} tasks</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Project filter */}
          {projects.length > 0 && (
            <select
              value={filterProjectId ?? ''}
              onChange={e => setFilterProjectId(e.target.value || null)}
              className="text-[12px] px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-[#344054] max-w-[180px]"
              style={{ fontWeight: 500 }}
            >
              <option value="">All projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.emoji} {p.title}</option>
              ))}
            </select>
          )}

          {/* Agent filter */}
          <select
            value={filterAgentId ?? ''}
            onChange={e => setFilterAgentId(e.target.value || null)}
            className="text-[12px] px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-[#344054]"
            style={{ fontWeight: 500 }}
          >
            <option value="">All agents</option>
            {agents.filter(a => a.isActive).map(a => (
              <option key={a.id} value={a.id}>{a.avatarEmoji} {a.name}</option>
            ))}
          </select>

          {/* View toggle */}
          <div className="flex bg-[#f3f4f6] rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('kanban')}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] transition-colors',
                viewMode === 'kanban' ? 'bg-white shadow-sm text-[#101828]' : 'text-[#667085]'
              )}
              style={{ fontWeight: 500 }}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] transition-colors',
                viewMode === 'list' ? 'bg-white shadow-sm text-[#101828]' : 'text-[#667085]'
              )}
              style={{ fontWeight: 500 }}
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* New task button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] text-white bg-[#be43ff] hover:bg-[#a035d9] transition-colors"
            style={{ fontWeight: 600 }}
          >
            <Plus className="w-3.5 h-3.5" />
            New Task
          </button>
        </div>
      </div>

      {/* Board content */}
      <div className="flex-1 overflow-hidden flex min-h-0 relative">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#be43ff]" />
          </div>
        ) : viewMode === 'kanban' ? (
          /* ── Kanban View ── */
          <div className="flex-1 flex gap-2 px-3 py-4 min-w-0 overflow-hidden">
            {COLUMNS.map(col => {
              const columnTasks = filteredTasks.filter(t =>
                col.status === 'working'
                  ? t.status === 'working' || t.status === 'waiting'
                  : t.status === col.status
              );
              return (
                <div key={col.status} className="flex-1 min-w-0 flex flex-col min-h-0">
                  {/* Column header */}
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: col.color }}
                    />
                    <span className="text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>
                      {col.label}
                    </span>
                    <span className="text-[11px] text-[#98a2b3] ml-auto">
                      {columnTasks.length}
                    </span>
                  </div>

                  {/* Column cards */}
                  <div className="flex-1 space-y-2 overflow-y-auto pb-4 pr-1">
                    {columnTasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        project={task.projectId ? projectsById.get(task.projectId) : null}
                        onClick={() => setSelectedTaskId(task.id)}
                        onForceStart={() => handleForceStart(task.id)}
                        onDelete={() => handleDeleteTask(task.id)}
                        onMarkDone={() => handleMarkDone(task.id)}
                      />
                    ))}
                    {columnTasks.length === 0 && (
                      <div className="text-center py-8 text-[11px] text-[#d0d5dd]">
                        No tasks
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── List View ── */
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="max-w-[960px] mx-auto">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_110px_110px_70px_90px_80px_40px] gap-2 px-3 py-2 text-[10px] text-[#98a2b3] uppercase tracking-wider border-b border-gray-100" style={{ fontWeight: 600 }}>
                <span>Task</span>
                <span>Project</span>
                <span>Agent</span>
                <span>Priority</span>
                <span>Deadline</span>
                <span>Status</span>
                <span></span>
              </div>
              <div className="space-y-0.5">
                {filteredTasks.map(task => {
                  const statusCol = COLUMNS.find(c => c.status === task.status);
                  const pri = PRIORITY_CONFIG[task.priority];
                  const StatusIcon = STATUS_ICONS[task.status];
                  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';
                  const taskProject = task.projectId ? projectsById.get(task.projectId) : null;
                  return (
                    <div
                      key={task.id}
                      className="grid grid-cols-[1fr_110px_110px_70px_90px_80px_40px] gap-2 items-center px-3 py-2.5 rounded-xl hover:bg-[#f9fafb] transition-colors group"
                    >
                      <button
                        onClick={() => setSelectedTaskId(task.id)}
                        className="flex items-center gap-2 min-w-0 text-left"
                      >
                        <StatusIcon
                          className={cn('w-3.5 h-3.5 shrink-0', task.status === 'working' && 'animate-spin')}
                          style={{ color: statusCol?.color }}
                        />
                        <span className="text-[13px] text-[#101828] truncate" style={{ fontWeight: 500 }}>
                          {task.title}
                        </span>
                        {(task.subtaskCount ?? 0) > 0 && (
                          <span className="text-[10px] text-[#98a2b3] shrink-0">+{task.subtaskCount}</span>
                        )}
                      </button>
                      <div className="flex items-center gap-1 min-w-0" title={taskProject?.title}>
                        {taskProject ? (
                          <>
                            <span className="text-[11px] shrink-0">{taskProject.emoji}</span>
                            <span className="text-[11px] text-[#667085] truncate" style={{ fontWeight: 500 }}>
                              {taskProject.title}
                            </span>
                          </>
                        ) : (
                          <span className="text-[11px] text-[#d0d5dd]">—</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {task.assignedAgent && (
                          <>
                            <AgentAvatar agent={task.assignedAgent} size={14} />
                            <span className="text-[11px] text-[#667085] truncate" style={{ fontWeight: 500 }}>
                              {task.assignedAgent.name}
                            </span>
                          </>
                        )}
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded w-fit" style={{ background: pri.bg, color: pri.color, fontWeight: 600 }}>
                        {pri.label}
                      </span>
                      <span className={cn('text-[11px]', isOverdue ? 'text-red-500' : 'text-[#98a2b3]')} style={{ fontWeight: 500 }}>
                        {task.deadline
                          ? new Date(task.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : '—'}
                      </span>
                      <span className="text-[11px]" style={{ color: statusCol?.color, fontWeight: 600 }}>
                        {statusCol?.label}
                      </span>
                      {/* Row actions */}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {task.status !== 'working' && (
                          <button
                            onClick={() => handleForceStart(task.id)}
                            className="p-1 rounded text-green-500 hover:bg-green-50 transition-colors"
                            title="Force Start"
                          >
                            <Play className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-1 rounded text-red-400 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {filteredTasks.length === 0 && (
                  <div className="text-center py-16 text-[13px] text-[#c4c9d4]">
                    No tasks yet. Create one or ask an agent to delegate work.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detail panel — overlays from right */}
        {selectedTaskId && (
          <div className="absolute inset-y-0 right-0 z-20 w-[50%] shadow-[-4px_0_24px_rgba(0,0,0,0.08)]">
            <TaskDetailPanel
              taskId={selectedTaskId}
              agents={agents}
              onClose={() => setSelectedTaskId(null)}
              onUpdate={loadTasks}
              onDelete={() => { setSelectedTaskId(null); loadTasks(); }}
              brandId={brandId}
            />
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <CreateTaskModal
          agents={agents}
          onClose={() => setShowCreateModal(false)}
          onCreate={() => loadTasks()}
          brandId={brandId}
        />
      )}
    </div>
  );
}
